// C4 — Programación de Producción del cliente (Alpina).
//
// Estructura:
//   - SemanaNav (← anterior / Hoy / siguiente →)
//   - Tabla de versiones agrupadas por cliente
//   - Acciones:
//       Nueva programación (modal con form de detalles)
//       Comparar 2 versiones (modal de diff)
//       Registrar pedido extra (modal con tipo)
//   - Sección "Pedidos extra de la semana" (tabla)
//
// Versionado: cada POST crea una versión nueva y cierra la anterior. La UI
// muestra cuál es "VIGENTE" y cuáles "REEMPLAZADAS".

import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  semanaOperativaKeyDe,
  type CoordinadorProgramacionProduccionSemana,
  type PedidoExtra,
  type PedidoExtraTipo,
  type ProgramacionDiff,
  type ProgramacionResumen,
} from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import {
  useCoordinadorPedidosExtra,
  useCoordinadorProgramaciones,
  useCrearPedidoExtra,
  useCrearProgramacion,
  useProgramacionDetalle,
  useProgramacionDiff,
} from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { avisoTotalSospechoso, vigenteComparable } from './c4AvisoTotales';
import { cn } from '../../lib/utils';
import { C4ImportarExcelDialog } from './C4ImportarExcelDialog';

type ModalState =
  | { kind: 'cerrado' }
  | { kind: 'nueva-prog' }
  | { kind: 'importar-excel' }
  | { kind: 'detalle-prog'; id: string }
  | { kind: 'pedido-extra' }
  | { kind: 'diff'; from: string; to: string };

export function C4ProgramacionProduccionPage() {
  const { state, signOut } = useAuth();
  const [semanaIso, setSemanaIso] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: 'cerrado' });
  // Selección para comparar versiones — 0 a 2 ids
  const [seleccion, setSeleccion] = useState<string[]>([]);

  const { data, isLoading, isError, error } = useCoordinadorProgramaciones(semanaIso);
  const pedidosQ = useCoordinadorPedidosExtra(semanaIso);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando programaciones…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => signOut()} variant="outline">
          Cerrar sesión
        </Button>
      </main>
    );
  }

  const toggleSeleccion = (id: string) => {
    setSeleccion((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < 2
          ? [...prev, id]
          : [prev[1]!, id],
    );
  };

  const abrirDiff = () => {
    if (seleccion.length !== 2) {
      toast.error('Elija 2 versiones para comparar');
      return;
    }
    setModal({ kind: 'diff', from: seleccion[0]!, to: seleccion[1]! });
  };

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="border-b border-border pb-3">
          <h1 className="text-xl font-medium text-fg">Programación de producción</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {data.tenant.nombre} · versionado de programaciones del cliente
          </p>
        </header>

        <SemanaNav
          semanaIso={data.semana_iso}
          dias={data.dias}
          onSemanaChange={(iso) => setSemanaIso(iso)}
        />

        <section className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setModal({ kind: 'nueva-prog' })}>+ Nueva programación</Button>
          <Button variant="outline" onClick={() => setModal({ kind: 'importar-excel' })}>
            Importar Excel
          </Button>
          <Button variant="outline" onClick={() => setModal({ kind: 'pedido-extra' })}>
            + Pedido extra
          </Button>
          <Button variant="outline" disabled={seleccion.length !== 2} onClick={abrirDiff}>
            Comparar versiones ({seleccion.length}/2)
          </Button>
        </section>

        {/* Lista de versiones */}
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-tertiary">
            Versiones cargadas
          </h2>
          {data.programaciones.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                No hay programaciones cargadas para esta semana.
              </CardContent>
            </Card>
          ) : (
            <TablaVersiones
              programaciones={data.programaciones}
              seleccion={seleccion}
              onToggleSeleccion={toggleSeleccion}
              onVerDetalle={(id) => setModal({ kind: 'detalle-prog', id })}
            />
          )}
        </section>

        {/* Pedidos extra */}
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-tertiary">
            Pedidos extra de la semana ({pedidosQ.data?.pedidos.length ?? 0})
          </h2>
          {pedidosQ.isLoading ? (
            <Spinner className="size-4" />
          ) : pedidosQ.data && pedidosQ.data.pedidos.length > 0 ? (
            <TablaPedidosExtra pedidos={pedidosQ.data.pedidos} />
          ) : (
            <Card>
              <CardContent className="py-4 text-center text-xs text-fg-tertiary">
                Sin pedidos extra cargados esta semana.
              </CardContent>
            </Card>
          )}
        </section>

        {modal.kind === 'nueva-prog' ? (
          <ModalNuevaProgramacion data={data} onClose={() => setModal({ kind: 'cerrado' })} />
        ) : null}

        {modal.kind === 'importar-excel' ? (
          <C4ImportarExcelDialog data={data} onClose={() => setModal({ kind: 'cerrado' })} />
        ) : null}

        {modal.kind === 'detalle-prog' ? (
          <ModalDetalleProgramacion id={modal.id} onClose={() => setModal({ kind: 'cerrado' })} />
        ) : null}

        {modal.kind === 'pedido-extra' ? (
          <ModalPedidoExtra data={data} onClose={() => setModal({ kind: 'cerrado' })} />
        ) : null}

        {modal.kind === 'diff' ? (
          <ModalDiff
            fromId={modal.from}
            toId={modal.to}
            onClose={() => setModal({ kind: 'cerrado' })}
          />
        ) : null}
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// SemanaNav (reusa patrón de C2/C3)
// =============================================================================

function SemanaNav({
  semanaIso,
  dias,
  onSemanaChange,
}: {
  semanaIso: string;
  dias: string[];
  onSemanaChange: (semanaIso: string | null) => void;
}) {
  const { diaInicioSemana } = useTenantConfig();
  // Primer y último día de la semana OPERATIVA (el backend manda el orden).
  const inicioSemana = dias[0]!;
  const finSemana = dias[6]!;
  const irOffset = (deltaSemanas: number) => {
    // Clave de la semana operativa vecina: inicio ± 7 días → clave del tenant.
    const d = new Date(inicioSemana + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + deltaSemanas * 7);
    return semanaOperativaKeyDe(d.toISOString().slice(0, 10), diaInicioSemana);
  };
  return (
    <div className="flex items-center justify-between gap-2">
      <Button variant="outline" size="sm" onClick={() => onSemanaChange(irOffset(-1))}>
        ← Semana anterior
      </Button>
      <div className="text-center">
        <p className="text-sm font-medium text-fg">
          {semanaIso} · {fmtDia(inicioSemana)} a {fmtDia(finSemana)}
        </p>
        <button
          type="button"
          onClick={() => onSemanaChange(null)}
          className="text-xs text-fg-tertiary underline hover:text-fg-secondary"
        >
          Hoy
        </button>
      </div>
      <Button variant="outline" size="sm" onClick={() => onSemanaChange(irOffset(1))}>
        Semana siguiente →
      </Button>
    </div>
  );
}

function fmtDia(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return `${d.getUTCDate()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// =============================================================================
// TablaVersiones
// =============================================================================

function TablaVersiones({
  programaciones,
  seleccion,
  onToggleSeleccion,
  onVerDetalle,
}: {
  programaciones: ProgramacionResumen[];
  seleccion: string[];
  onToggleSeleccion: (id: string) => void;
  onVerDetalle: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-surface-overlay text-xs uppercase text-fg-secondary">
            <th className="border-b border-border px-2 py-2 text-center w-10">✓</th>
            <th className="border-b border-border px-3 py-2 text-left">Cliente</th>
            <th className="border-b border-border px-3 py-2 text-left">Planta</th>
            <th className="border-b border-border px-3 py-2 text-left">Versión</th>
            <th className="border-b border-border px-3 py-2 text-left">Estado</th>
            <th className="border-b border-border px-3 py-2 text-right">Paquetes</th>
            <th className="border-b border-border px-3 py-2 text-right">Filas detalle</th>
            <th className="border-b border-border px-3 py-2 text-left">Cargada por</th>
            <th className="border-b border-border px-3 py-2 text-left">Origen</th>
            <th className="border-b border-border px-3 py-2 text-right">Acción</th>
          </tr>
        </thead>
        <tbody>
          {programaciones.map((p) => {
            const vigente = p.vigente_hasta === null;
            const seleccionada = seleccion.includes(p.id);
            return (
              <tr
                key={p.id}
                className={cn('border-b border-border', vigente ? 'bg-ok-soft/30' : '')}
              >
                <td className="px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={seleccionada}
                    onChange={() => onToggleSeleccion(p.id)}
                    aria-label={`Seleccionar versión ${p.version}`}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-fg">{p.cliente_nombre}</td>
                <td className="px-3 py-2 text-xs text-fg-secondary">
                  {p.planta_nombre ?? 'Todas'}
                </td>
                <td className="px-3 py-2">v{p.version}</td>
                <td className="px-3 py-2">
                  {vigente ? (
                    <span className="rounded bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-900">
                      Vigente
                    </span>
                  ) : (
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fg-secondary">
                      Reemplazada
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {p.total_paquetes.toLocaleString('es-CO')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{p.total_lineas_detalle}</td>
                <td className="px-3 py-2 text-xs text-fg-secondary">
                  {p.cargada_por_nombre} {p.cargada_por_apellido}
                </td>
                <td className="px-3 py-2 text-xs text-fg-secondary">{p.origen}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onVerDetalle(p.id)}
                    className="text-xs text-fg-secondary underline hover:text-fg"
                  >
                    Ver detalle
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// TablaPedidosExtra
// =============================================================================

function TablaPedidosExtra({ pedidos }: { pedidos: PedidoExtra[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-surface-overlay text-xs uppercase text-fg-secondary">
            <th className="border-b border-border px-3 py-2 text-left">Cliente</th>
            <th className="border-b border-border px-3 py-2 text-left">Fecha para</th>
            <th className="border-b border-border px-3 py-2 text-left">Línea</th>
            <th className="border-b border-border px-3 py-2 text-left">SKU</th>
            <th className="border-b border-border px-3 py-2 text-right">Paquetes</th>
            <th className="border-b border-border px-3 py-2 text-left">Tipo</th>
            <th className="border-b border-border px-3 py-2 text-left">Registró</th>
          </tr>
        </thead>
        <tbody>
          {pedidos.map((p) => (
            <tr key={p.id} className="border-b border-border">
              <td className="px-3 py-2 font-medium text-fg">{p.cliente_nombre}</td>
              <td className="px-3 py-2">{fmtDia(p.fecha_para)}</td>
              <td className="px-3 py-2">{p.linea_codigo}</td>
              <td className="px-3 py-2 text-xs">
                {p.sku_codigo_cliente} {p.sku_descripcion}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {p.cantidad_solicitada_paquetes.toLocaleString('es-CO')}
              </td>
              <td className="px-3 py-2">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                    p.tipo === 'nueva_version_programa'
                      ? 'bg-amber-200 text-amber-900'
                      : 'bg-slate-200 text-fg-secondary',
                  )}
                >
                  {p.tipo === 'nueva_version_programa' ? 'nueva versión' : 'evento'}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-fg-secondary">{p.registrado_por_nombre}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// ModalNuevaProgramacion
// =============================================================================

type DetalleRow = {
  fecha: string;
  area_id: string;
  sku_id: string;
  cantidad_paquetes: string;
};

function ModalNuevaProgramacion({
  data,
  onClose,
}: {
  data: CoordinadorProgramacionProduccionSemana;
  onClose: () => void;
}) {
  const [clienteId, setClienteId] = useState(data.clientes[0]?.id ?? '');
  const [filas, setFilas] = useState<DetalleRow[]>([
    { fecha: data.dias[0]!, area_id: '', sku_id: '', cantidad_paquetes: '' },
  ]);
  const crear = useCrearProgramacion();

  // Sanity check: total nuevo vs versión vigente de (cliente, planta). El
  // primer click en guardar muestra el aviso; el segundo confirma.
  const [avisoConfirmado, setAvisoConfirmado] = useState(false);
  useEffect(() => setAvisoConfirmado(false), [filas, clienteId]);
  const aviso = useMemo(() => {
    const totalNuevo = filas.reduce((acc, f) => acc + (Number(f.cantidad_paquetes) || 0), 0);
    const primeraArea = data.areas.find((a) => a.id === filas[0]?.area_id);
    const vigente = vigenteComparable(
      data.programaciones,
      clienteId,
      primeraArea?.planta_id ?? null,
    );
    return vigente ? avisoTotalSospechoso(totalNuevo, vigente.total_paquetes) : null;
  }, [filas, clienteId, data.areas, data.programaciones]);

  // SKUs filtrados por cliente seleccionado
  const skusDelCliente = useMemo(
    () => data.skus.filter((s) => s.cliente_id === clienteId),
    [data.skus, clienteId],
  );

  const agregarFila = () => {
    setFilas((prev) => [
      ...prev,
      { fecha: data.dias[0]!, area_id: '', sku_id: '', cantidad_paquetes: '' },
    ]);
  };

  const borrarFila = (i: number) => {
    setFilas((prev) => prev.filter((_, idx) => idx !== i));
  };

  const updateFila = (i: number, patch: Partial<DetalleRow>) => {
    setFilas((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  };

  const handleGuardar = async () => {
    if (!clienteId) {
      toast.error('Elija un cliente');
      return;
    }
    const detalles = filas.map((f) => ({
      fecha: f.fecha,
      area_id: f.area_id,
      sku_id: f.sku_id,
      cantidad_paquetes: Number(f.cantidad_paquetes),
    }));
    // Validación FE básica
    for (const [i, d] of detalles.entries()) {
      if (!d.area_id || !d.sku_id || !d.cantidad_paquetes || d.cantidad_paquetes <= 0) {
        toast.error(`Fila ${i + 1}: complete área, SKU y cantidad > 0`);
        return;
      }
    }
    if (aviso && !avisoConfirmado) {
      setAvisoConfirmado(true);
      return;
    }
    try {
      const result = await crear.mutateAsync({
        cliente_id: clienteId,
        semana_iso: data.semana_iso,
        detalles,
      });
      toast.success(`Programación v${result.version} cargada`);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error desconocido');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Nueva programación · {data.semana_iso}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <Label htmlFor="cliente">Cliente</Label>
            <Select id="cliente" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              {data.clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre} {c.es_principal ? '(principal)' : ''}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold uppercase text-fg-tertiary">
              Detalles ({filas.length} {filas.length === 1 ? 'fila' : 'filas'})
            </p>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-overlay text-xs uppercase text-fg-secondary">
                    <th className="px-2 py-1 text-left">Fecha</th>
                    <th className="px-2 py-1 text-left">Área</th>
                    <th className="px-2 py-1 text-left">SKU</th>
                    <th className="px-2 py-1 text-right">Paquetes</th>
                    <th className="px-2 py-1 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i} className="border-b border-border">
                      <td className="px-1 py-1">
                        <Select
                          value={f.fecha}
                          onChange={(e) => updateFila(i, { fecha: e.target.value })}
                        >
                          {data.dias.map((d) => (
                            <option key={d} value={d}>
                              {fmtDia(d)}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="px-1 py-1">
                        <Select
                          value={f.area_id}
                          onChange={(e) => updateFila(i, { area_id: e.target.value })}
                        >
                          <option value="">— Área —</option>
                          {data.areas.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.codigo} {a.nombre}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="px-1 py-1">
                        <Select
                          value={f.sku_id}
                          onChange={(e) => updateFila(i, { sku_id: e.target.value })}
                        >
                          <option value="">— SKU —</option>
                          {skusDelCliente.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.codigo_cliente} {s.descripcion}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number"
                          min={1}
                          value={f.cantidad_paquetes}
                          onChange={(e) => updateFila(i, { cantidad_paquetes: e.target.value })}
                          className="text-right tabular-nums"
                        />
                      </td>
                      <td className="px-1 py-1">
                        {filas.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => borrarFila(i)}
                            className="text-xs text-crit hover:text-red-900"
                            aria-label="Borrar fila"
                          >
                            ✕
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="outline" size="sm" onClick={agregarFila} className="mt-2">
              + Agregar fila
            </Button>
          </div>
        </div>
        {aviso && avisoConfirmado ? (
          <p className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            ⚠ {aviso}
          </p>
        ) : null}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={crear.isPending}>
            {crear.isPending
              ? 'Guardando…'
              : aviso && avisoConfirmado
                ? 'Crear de todos modos'
                : 'Crear nueva versión'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// ModalDetalleProgramacion
// =============================================================================

const DOW_CORTO = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
function fmtDiaSemana(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return `${DOW_CORTO[d.getUTCDay()]} ${fmtDia(iso)}`;
}
const fmtPaq = (n: number) => n.toLocaleString('es-CO');
const fmtKg = (n: number) => n.toLocaleString('es-CO', { maximumFractionDigits: 0 });

function ModalDetalleProgramacion({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useProgramacionDetalle(id);
  // Celda seleccionada para drill (área × día → SKUs).
  const [celdaSel, setCeldaSel] = useState<{ areaId: string; fecha: string } | null>(null);

  // KPIs de cabecera.
  const kpis = useMemo(() => {
    if (!data) return null;
    const det = data.detalles;
    return {
      dias: new Set(det.map((d) => d.fecha)).size,
      areas: new Set(det.map((d) => d.area_codigo)).size,
      skus: new Set(det.map((d) => d.sku_id)).size,
      kgTotal: det.reduce((s, d) => s + (d.cantidad_kg ?? 0), 0),
      hayKg: det.some((d) => d.cantidad_kg != null),
    };
  }, [data]);

  // Matriz área × día. Celda = Σ paquetes; guarda filas para el drill a SKUs.
  // Fechas en orden cronológico (backend ordena por fecha); áreas por código.
  const matriz = useMemo(() => {
    if (!data) return null;
    const fechas: string[] = [];
    const fechaVista = new Set<string>();
    const areasMap = new Map<string, { id: string; codigo: string; nombre: string }>();
    const celdas = new Map<string, { paquetes: number; kg: number; filas: typeof data.detalles }>();
    const totalFecha = new Map<string, number>();
    const totalArea = new Map<string, number>();
    for (const d of data.detalles) {
      if (!fechaVista.has(d.fecha)) {
        fechaVista.add(d.fecha);
        fechas.push(d.fecha);
      }
      if (!areasMap.has(d.area_id)) {
        areasMap.set(d.area_id, {
          id: d.area_id,
          codigo: d.area_codigo,
          nombre: d.area_nombre,
        });
      }
      const key = `${d.area_id}|${d.fecha}`;
      let c = celdas.get(key);
      if (!c) {
        c = { paquetes: 0, kg: 0, filas: [] };
        celdas.set(key, c);
      }
      c.paquetes += d.cantidad_paquetes;
      c.kg += d.cantidad_kg ?? 0;
      c.filas.push(d);
      totalFecha.set(d.fecha, (totalFecha.get(d.fecha) ?? 0) + d.cantidad_paquetes);
      totalArea.set(d.area_id, (totalArea.get(d.area_id) ?? 0) + d.cantidad_paquetes);
    }
    const areas = [...areasMap.values()].sort((a, b) => a.codigo.localeCompare(b.codigo));
    let maxCelda = 1;
    for (const c of celdas.values()) if (c.paquetes > maxCelda) maxCelda = c.paquetes;
    return { fechas, areas, celdas, totalFecha, totalArea, maxCelda };
  }, [data]);

  const mostrarKg = kpis?.hayKg ?? false;
  const drill =
    celdaSel && matriz
      ? {
          area: matriz.areas.find((a) => a.id === celdaSel.areaId) ?? null,
          fecha: celdaSel.fecha,
          celda: matriz.celdas.get(`${celdaSel.areaId}|${celdaSel.fecha}`) ?? null,
        }
      : null;

  return (
    <Dialog open onClose={onClose} className="max-w-[1100px]">
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>
              {data ? `${data.cliente_nombre} · v${data.version}` : 'Cargando detalle…'}
            </DialogTitle>
            {data && (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                  data.vigente_hasta ? 'bg-surface-overlay text-fg-tertiary' : 'bg-ok/15 text-ok',
                )}
              >
                {data.vigente_hasta ? 'Reemplazada' : 'Vigente'}
              </span>
            )}
          </div>
        </DialogHeader>
        {isLoading || !data || !matriz ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="size-6" />
          </div>
        ) : (
          <>
            <p className="text-xs text-fg-tertiary">
              {data.semana_iso} · vigente desde {data.vigente_desde.slice(0, 16).replace('T', ' ')}
              {data.vigente_hasta
                ? ` · reemplazada el ${data.vigente_hasta.slice(0, 16).replace('T', ' ')}`
                : ''}
            </p>
            <p className="text-xs text-fg-tertiary">
              Cargada por {data.cargada_por_nombre} {data.cargada_por_apellido} · origen{' '}
              {data.origen}
              {data.planta_nombre ? ` · planta ${data.planta_nombre}` : ''}
            </p>

            {/* Tira de KPIs */}
            {kpis && (
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                {[
                  { label: 'Días', value: fmtPaq(kpis.dias), destacado: false },
                  { label: 'Áreas', value: fmtPaq(kpis.areas), destacado: false },
                  { label: 'SKUs', value: fmtPaq(kpis.skus), destacado: false },
                  { label: 'Paquetes', value: fmtPaq(data.total_paquetes), destacado: true },
                  ...(mostrarKg
                    ? [{ label: 'Kilos', value: fmtKg(kpis.kgTotal), destacado: false }]
                    : []),
                ].map((k) => (
                  <div
                    key={k.label}
                    className={cn(
                      'rounded border px-3 py-2',
                      k.destacado
                        ? 'border-info/40 bg-info/10'
                        : 'border-border bg-surface-overlay/40',
                    )}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {k.label}
                    </div>
                    <div className="mt-0.5 text-lg font-semibold tabular-nums text-fg">
                      {k.value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Matriz Día × Área (celda = paquetes; intensidad = heatmap) */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-fg-tertiary">
                Distribución semanal · paquetes por área y día
              </p>
              <p className="text-[11px] text-fg-tertiary">Clic en una celda para ver los SKUs →</p>
            </div>
            <div className="mt-2 max-h-[48vh] overflow-auto rounded border border-border">
              <table className="border-collapse text-sm">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-surface-overlay text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
                    <th className="sticky left-0 z-30 min-w-[10rem] border-b border-r-2 border-border-strong bg-surface-overlay px-3 py-2.5 text-left">
                      Área
                    </th>
                    {matriz.fechas.map((f) => (
                      <th
                        key={f}
                        className="min-w-[6rem] border-b border-l border-border bg-surface-overlay px-3 py-2.5 text-right"
                      >
                        {fmtDiaSemana(f)}
                      </th>
                    ))}
                    <th className="min-w-[7rem] border-b border-l-2 border-border-strong bg-surface-overlay px-3 py-2.5 text-right text-fg-secondary">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {matriz.areas.map((l, idx) => {
                    const zebra = idx % 2 === 1;
                    const bgFila = zebra ? 'bg-surface-elevated' : 'bg-surface-base';
                    return (
                      <tr key={l.id} className="group">
                        <th
                          className={cn(
                            'sticky left-0 z-10 min-w-[10rem] border-b border-r-2 border-border-strong px-3 py-2 text-left align-top transition-colors',
                            bgFila,
                            'group-hover:bg-surface-overlay',
                          )}
                        >
                          <div className="font-medium text-fg">{l.codigo}</div>
                          <div className="text-[11px] font-normal text-fg-tertiary">{l.nombre}</div>
                        </th>
                        {matriz.fechas.map((f) => {
                          const c = matriz.celdas.get(`${l.id}|${f}`);
                          const sel = celdaSel?.areaId === l.id && celdaSel.fecha === f;
                          if (!c) {
                            return (
                              <td
                                key={f}
                                className={cn(
                                  'border-b border-l border-border/50 px-3 py-2 text-right text-fg-tertiary/40',
                                  bgFila,
                                )}
                              >
                                —
                              </td>
                            );
                          }
                          // Heatmap NEUTRO (info/azul), NO accent rojo: el rojo se
                          // reserva para alertas reales. La intensidad solo indica volumen.
                          const pct = Math.round((c.paquetes / matriz.maxCelda) * 45);
                          return (
                            <td
                              key={f}
                              onClick={() => setCeldaSel(sel ? null : { areaId: l.id, fecha: f })}
                              title={`${c.filas.length} SKU${c.filas.length === 1 ? '' : 's'} · ${fmtPaq(c.paquetes)} paq`}
                              style={{
                                backgroundColor: `color-mix(in srgb, var(--info) ${pct}%, transparent)`,
                              }}
                              className={cn(
                                'cursor-pointer border-b border-l border-border/50 px-3 py-2 text-right tabular-nums text-fg transition-all hover:brightness-125',
                                sel && 'outline outline-2 -outline-offset-2 outline-fg',
                              )}
                            >
                              {fmtPaq(c.paquetes)}
                            </td>
                          );
                        })}
                        <td
                          className={cn(
                            'border-b border-l-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums text-fg transition-colors',
                            bgFila,
                            'group-hover:bg-surface-overlay',
                          )}
                        >
                          {fmtPaq(matriz.totalArea.get(l.id) ?? 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 z-20">
                  <tr className="border-t-2 border-border-strong bg-surface-overlay font-semibold text-fg">
                    <th className="sticky left-0 z-30 border-r-2 border-border-strong bg-surface-overlay px-3 py-2 text-left text-xs uppercase tracking-wider text-fg-secondary">
                      Total
                    </th>
                    {matriz.fechas.map((f) => (
                      <td
                        key={f}
                        className="border-l border-border px-3 py-2 text-right tabular-nums"
                      >
                        {fmtPaq(matriz.totalFecha.get(f) ?? 0)}
                      </td>
                    ))}
                    <td className="border-l-2 border-border-strong px-3 py-2 text-right tabular-nums text-fg">
                      {fmtPaq(data.total_paquetes)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Drill: SKUs de la celda seleccionada */}
            {drill && drill.celda && drill.area && (
              <div className="mt-3 rounded border border-info/40 bg-surface-overlay/40">
                <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                  <div className="text-sm">
                    <span className="font-semibold text-fg">{drill.area.codigo}</span>{' '}
                    <span className="text-fg-tertiary">
                      · {fmtDiaSemana(drill.fecha)} · {drill.celda.filas.length}{' '}
                      {drill.celda.filas.length === 1 ? 'SKU' : 'SKUs'} ·{' '}
                      {fmtPaq(drill.celda.paquetes)} paq
                    </span>
                  </div>
                  <button
                    onClick={() => setCeldaSel(null)}
                    className="rounded p-1 text-fg-tertiary transition-colors hover:bg-surface-overlay hover:text-fg"
                    aria-label="Cerrar detalle de celda"
                  >
                    ✕
                  </button>
                </div>
                <div className="max-h-[26vh] overflow-y-auto">
                  <table className="w-full table-fixed border-collapse text-sm">
                    <colgroup>
                      <col />
                      <col className="w-[7rem]" />
                      {mostrarKg && <col className="w-[6rem]" />}
                    </colgroup>
                    <thead className="sticky top-0 z-10">
                      <tr className="border-b border-border bg-surface-overlay text-left text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
                        <th className="px-3 py-2">SKU</th>
                        <th className="px-3 py-2 text-right">Paquetes</th>
                        {mostrarKg && <th className="px-3 py-2 text-right">Kilos</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {drill.celda.filas.map((d, i) => (
                        <tr
                          key={`${d.sku_id}-${i}`}
                          className={cn(
                            'border-b border-border/40',
                            i % 2 === 1 && 'bg-surface-elevated/40',
                          )}
                        >
                          <td className="px-3 py-1.5">
                            <div className="font-medium text-fg">{d.sku_codigo_cliente}</div>
                            <div className="text-xs text-fg-tertiary">{d.sku_descripcion}</div>
                          </td>
                          <td className="px-3 py-1.5 text-right align-top tabular-nums text-fg">
                            {fmtPaq(d.cantidad_paquetes)}
                          </td>
                          {mostrarKg && (
                            <td className="px-3 py-1.5 text-right align-top tabular-nums text-fg-tertiary">
                              {d.cantidad_kg != null ? fmtKg(d.cantidad_kg) : '—'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// ModalDiff
// =============================================================================

function ModalDiff({
  fromId,
  toId,
  onClose,
}: {
  fromId: string;
  toId: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error } = useProgramacionDiff(fromId, toId);
  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Comparar versiones</DialogTitle>
        </DialogHeader>
        {isLoading || !data ? (
          isError ? (
            <p className="text-sm text-crit">{error instanceof Error ? error.message : 'Error'}</p>
          ) : (
            <Spinner className="size-6" />
          )
        ) : (
          <DiffContent diff={data} />
        )}
        <DialogFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffContent({ diff }: { diff: ProgramacionDiff }) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-center gap-3 text-xs text-fg-secondary">
        <div className="rounded bg-surface-overlay px-3 py-2">
          <div className="font-semibold text-fg">
            {diff.from.cliente_nombre} · v{diff.from.version}
          </div>
          <div>{diff.from.total_paquetes.toLocaleString('es-CO')} paquetes</div>
        </div>
        <div className="text-xl">→</div>
        <div className="rounded bg-surface-overlay px-3 py-2">
          <div className="font-semibold text-fg">
            {diff.to.cliente_nombre} · v{diff.to.version}
          </div>
          <div>{diff.to.total_paquetes.toLocaleString('es-CO')} paquetes</div>
        </div>
      </div>

      <DiffSeccion
        titulo={`Agregados (${diff.agregados.length})`}
        tono="emerald"
        items={diff.agregados.map((a) => ({
          fecha: a.fecha,
          area: a.area_codigo,
          sku: `${a.sku_codigo_cliente} ${a.sku_descripcion}`,
          info: `+${a.cantidad_paquetes_nueva.toLocaleString('es-CO')} paquetes`,
        }))}
      />

      <DiffSeccion
        titulo={`Cambiados (${diff.cambiados.length})`}
        tono="amber"
        items={diff.cambiados.map((c) => ({
          fecha: c.fecha,
          area: c.area_codigo,
          sku: `${c.sku_codigo_cliente} ${c.sku_descripcion}`,
          info: `${c.cantidad_paquetes_anterior.toLocaleString('es-CO')} → ${c.cantidad_paquetes_nueva.toLocaleString('es-CO')} (Δ ${c.delta > 0 ? '+' : ''}${c.delta.toLocaleString('es-CO')})`,
        }))}
      />

      <DiffSeccion
        titulo={`Eliminados (${diff.eliminados.length})`}
        tono="red"
        items={diff.eliminados.map((e) => ({
          fecha: e.fecha,
          area: e.area_codigo,
          sku: `${e.sku_codigo_cliente} ${e.sku_descripcion}`,
          info: `-${e.cantidad_paquetes_anterior.toLocaleString('es-CO')} paquetes`,
        }))}
      />
    </div>
  );
}

function DiffSeccion({
  titulo,
  tono,
  items,
}: {
  titulo: string;
  tono: 'emerald' | 'amber' | 'red';
  items: { fecha: string; area: string; sku: string; info: string }[];
}) {
  if (items.length === 0) return null;
  const colorBg =
    tono === 'emerald' ? 'bg-ok-soft' : tono === 'amber' ? 'bg-warn-soft' : 'bg-crit-soft';
  const colorTitle =
    tono === 'emerald' ? 'text-emerald-900' : tono === 'amber' ? 'text-amber-900' : 'text-red-900';
  return (
    <div className={cn('rounded border border-border', colorBg)}>
      <div className={cn('px-3 py-1 text-xs font-semibold uppercase', colorTitle)}>{titulo}</div>
      <ul className="divide-y divide-slate-200 text-xs">
        {items.map((it, i) => (
          <li key={i} className="px-3 py-1.5">
            <span className="font-medium">{fmtDia(it.fecha)}</span> · {it.area} · {it.sku} ·{' '}
            <span className="font-mono">{it.info}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// ModalPedidoExtra
// =============================================================================

function ModalPedidoExtra({
  data,
  onClose,
}: {
  data: CoordinadorProgramacionProduccionSemana;
  onClose: () => void;
}) {
  const [clienteId, setClienteId] = useState(data.clientes[0]?.id ?? '');
  const [fechaPara, setFechaPara] = useState(data.dias[0]!);
  const [lineaId, setLineaId] = useState('');
  const [skuId, setSkuId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [tipo, setTipo] = useState<PedidoExtraTipo>('evento_adicional');
  const [observaciones, setObservaciones] = useState('');
  const crear = useCrearPedidoExtra();

  const skusDelCliente = useMemo(
    () => data.skus.filter((s) => s.cliente_id === clienteId),
    [data.skus, clienteId],
  );

  const handleGuardar = async () => {
    if (!clienteId || !lineaId || !skuId || !cantidad || Number(cantidad) <= 0) {
      toast.error('Complete todos los campos');
      return;
    }
    try {
      await crear.mutateAsync({
        cliente_id: clienteId,
        fecha_para: fechaPara,
        linea_id: lineaId,
        sku_id: skuId,
        cantidad_solicitada_paquetes: Number(cantidad),
        tipo,
        observaciones: observaciones || null,
      });
      toast.success(
        tipo === 'nueva_version_programa'
          ? 'Pedido registrado + nueva versión creada'
          : 'Pedido registrado',
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error desconocido');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar pedido extra</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <Label htmlFor="cli">Cliente</Label>
            <Select id="cli" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              {data.clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="fp">Fecha para</Label>
            <Select id="fp" value={fechaPara} onChange={(e) => setFechaPara(e.target.value)}>
              {data.dias.map((d) => (
                <option key={d} value={d}>
                  {fmtDia(d)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="ln">Línea</Label>
            <Select id="ln" value={lineaId} onChange={(e) => setLineaId(e.target.value)}>
              <option value="">— Línea —</option>
              {data.lineas.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} {l.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="sku">SKU</Label>
            <Select id="sku" value={skuId} onChange={(e) => setSkuId(e.target.value)}>
              <option value="">— SKU —</option>
              {skusDelCliente.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.codigo_cliente} {s.descripcion}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="qty">Cantidad (paquetes)</Label>
            <Input
              id="qty"
              type="number"
              min={1}
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
            />
          </div>
          <div>
            <Label>Tipo</Label>
            <div className="flex flex-col gap-1 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="tipo"
                  checked={tipo === 'evento_adicional'}
                  onChange={() => setTipo('evento_adicional')}
                />
                <span>
                  <strong>Evento adicional</strong> — suma al programa actual sin versionar
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="tipo"
                  checked={tipo === 'nueva_version_programa'}
                  onChange={() => setTipo('nueva_version_programa')}
                />
                <span>
                  <strong>Nueva versión del programa</strong> — clona la vigente, agrega el ítem y
                  la deja como nueva versión
                </span>
              </label>
            </div>
          </div>
          <div>
            <Label htmlFor="obs">Observaciones (opcional)</Label>
            <Textarea
              id="obs"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={crear.isPending}>
            {crear.isPending ? 'Guardando…' : 'Registrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
